import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { ACCOUNT_USAGE_V2_URL } from 'api/BillingApi';
import { useEspAccountsJson } from 'api/ESPAccountsAPI';
import {
  useSearchParamState,
  serializationOptionsForStringValue,
} from 'utils/hooks/useSearchParamState';
import { useRestQuery } from 'utils/hooks/api/useRestQuery';
import { EspAccount } from 'modules/graphql/types';
import { addNegativeToast } from 'modules/toastContainer';
import { BillingQueryContext } from './BillingQueryContext';
import { convertDateToPST, remapCurrentMetrics, yesterday } from '../utils';
import { useDateSelection } from '../hooks';
import { Metric } from '../types';

const GET_USAGE_ERROR =
  'Trouble fetching usage data. Please try again, or contact your account manager.';

export type FetchEspAccountsResponse = {
  espAccounts: EspAccount[];
};

export const BillingQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch();
  const [usePreAggregatedData] = useSearchParamState({
    searchParamKey: 'usePreAggregatedData',
    stateValueWhenSearchParamIsUnset: '',
    pushChangesToBrowserHistory: true,
    ...serializationOptionsForStringValue,
  });
  const isUsePreAggregatedDataSet = ['true', 'false'].includes(usePreAggregatedData);

  const [useNfp] = useSearchParamState({
    searchParamKey: 'useNfp',
    stateValueWhenSearchParamIsUnset: '',
    pushChangesToBrowserHistory: true,
    ...serializationOptionsForStringValue,
  });

  const { dateRange, setDateRange, startDateTimePST, endDateTimePST } = useDateSelection();

  const { data: espAccounts = [] } = useEspAccountsJson();

  const {
    data: selectedData = {},
    isError: isErrorAccountUsage,
    isLoading: isLoadingAccountUsage,
  } = useRestQuery(
    ACCOUNT_USAGE_V2_URL,
    {
      startDateTime: startDateTimePST,
      endDateTime: endDateTimePST,
      ...(isUsePreAggregatedDataSet ? { usePreAggregatedData } : {}),
      ...(useNfp ? { useNfp } : {}),
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      onError: () => {
        dispatch(addNegativeToast(GET_USAGE_ERROR));
      },
    },
  );

  const {
    data: currentData = {},
    isError: isErrorCurrentAccountUsage,
    isLoading: isLoadingCurrentAccountUsage,
  } = useRestQuery(
    ACCOUNT_USAGE_V2_URL,
    {
      startDateTime: startDateTimePST,
      endDateTime: convertDateToPST(yesterday()),
      ...(isUsePreAggregatedDataSet ? { usePreAggregatedData } : {}),
      ...(useNfp ? { useNfp } : {}),
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      onError: () => {
        dispatch(addNegativeToast(GET_USAGE_ERROR));
      },
    },
  );

  const currentMetrics = remapCurrentMetrics(currentData.metrics);

  const selectedMetrics: Metric[] = selectedData.metrics || [];

  const metrics = [...selectedMetrics, ...currentMetrics];

  return (
    <BillingQueryContext.Provider
      value={{
        metrics,
        dateRange,
        setDateRange,
        isError: isErrorAccountUsage || isErrorCurrentAccountUsage,
        isLoading: isLoadingAccountUsage || isLoadingCurrentAccountUsage,
        espAccounts,
      }}
    >
      {children}
    </BillingQueryContext.Provider>
  );
};
